import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ttsService from '@/services/ttsService';
import chatService from '@/services/chatService';
import './Message.css';
import { assetUrl } from '@/utils/assetUrl';
import { CLICK_TAG_RE, parseClickAttrs } from '@/utils/clickProtocol';
const agentAvatarIcon = assetUrl('/assets/agent.svg');

const Message = ({ message, isUser, onClickableTextClick, lang = 'ar' }) => {
  const [playState, setPlayState] = useState('stopped'); // stopped, loading, playing
  const [feedback, setFeedback] = useState(null); // 'like' | 'dislike' | null
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const handleFeedbackClick = async (actionType) => {
    if (isSubmittingFeedback) return;
    const previous = feedback;
    const isToggleOff = feedback === actionType;
    const next = isToggleOff ? null : actionType;

    // Optimistic update
    setFeedback(next);

    // Backend (POST /v1/external/post-feedback) only accepts like/dislike/value;
    // there is no "remove feedback" action, so toggle-off is UI-only.
    if (isToggleOff) return;

    setIsSubmittingFeedback(true);

    try {
      await chatService.submitFeedback({
        messageText: message.text,
        action: next,
      });
    } catch (err) {
      // Revert on failure
      setFeedback(previous);
      console.error('Feedback submit failed:', err);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Detect if text is primarily English
  const isEnglishText = (text) => {
    // Remove HTML tags and special characters
    const cleanText = text.replace(/<[^>]*>/g, '').replace(/[^a-zA-Z\u0600-\u06FF\s]/g, '');
    const englishChars = (cleanText.match(/[a-zA-Z]/g) || []).length;
    const arabicChars = (cleanText.match(/[\u0600-\u06FF]/g) || []).length;
    
    // If more than 70% English characters, consider it English
    const totalChars = englishChars + arabicChars;
    if (totalChars === 0) return false;
    
    return englishChars / totalChars > 0.7;
  };

  const isEnglish = !isUser && isEnglishText(message.text);

  // Listen to TTS service state changes
  useEffect(() => {
    const handleStateChange = (messageId, state) => {
      if (messageId === message.id) {
        // This message's state changed
        setPlayState(state);
      } else if (state === 'playing' || state === 'loading') {
        // Another message started playing, stop this one's UI
        setPlayState('stopped');
      }
    };

    // Register listener and get unsubscribe function
    const unsubscribe = ttsService.onStateChange(handleStateChange);

    // Cleanup
    return () => {
      unsubscribe();
      
      // If this message was playing when unmounted, stop it
      const status = ttsService.getStatus();
      if (status.isPlaying && status.currentMessageId === message.id) {
        ttsService.stop();
      }
    };
  }, [message.id]);

  // Preprocess text to convert custom list formats to proper markdown
  const preprocessListFormat = (text) => {
    let processed = text;
    
    // Debug: log input (uncomment for debugging)
    // console.log('📝 [Preprocess] Input:', JSON.stringify(text.substring(0, 200)));
    
    // Step 1: Normalize line endings
    processed = processed.replace(/\r\n/g, '\n');
    
    // Step 2: Fix numbered list items - ensure each numbered item starts on its own line
    // Pattern: "text.  \n2." or "text. \n2." -> "text.\n\n2."
    // This specifically handles the markdown "  \n" line break before numbered items
    processed = processed.replace(/(\S)[ \t]*\n(\d+\.\s)/g, '$1\n\n$2');
    
    // Step 3: Fix bullet list items - remove trailing spaces for lines starting with •
    processed = processed.replace(/^([•].*?)[ \t]+$/gm, '$1');
    
    // Step 4: Handle "• N:" format (bullet + number + colon) - Arabic numbered bullet lists
    // Convert "• 1: text" to "1. text" for proper ordered list rendering
    processed = processed.replace(/^[•]\s*(\d+)\s*:\s*/gm, '$1. ');
    
    // Step 5: Handle inline "• N:" after colon (like "الخطوات: • 1: text")
    // This handles cases where the list starts on the same line as a header
    processed = processed.replace(/:\s*[•]\s*(\d+)\s*:\s*/g, ':\n\n$1. ');
    
    // Step 6: Handle bullet points with emoji numbers (• 1️⃣ text, • 2️⃣ text)
    // Remove bullet but keep the emoji number - don't mix bullets with numbers
    processed = processed.replace(/^[•]\s*([0-9]️⃣|🔟)/gm, '$1');
    
    // Step 7: Handle bullet points with regular numbers at start (• 1. text, • 1) text)
    // Remove bullet but keep the number
    processed = processed.replace(/^[•]\s*(\d+[.\)]\s*)/gm, '$1');
    
    // Step 8: Handle standalone bullet points WITHOUT numbers (• text)
    // Convert to standard markdown unordered list
    processed = processed.replace(/^[•]\s*/gm, '- ');
    
    // Step 9: Ensure there's a blank line before the FIRST list item after text
    // After a header with colon (not already followed by blank line)
    processed = processed.replace(/(:)\n(?!\n)(- |\d+\. |\d️⃣)/g, '$1\n\n$2');
    
    // Step 10: Ensure blank line before lists after non-list paragraphs
    // Match: end of sentence (not a list item), single newline, then list item
    processed = processed.replace(/([.،!?؟])\n(?!\n)(\d+\.\s)/g, '$1\n\n$2');
    
    // Debug: log output (uncomment for debugging)
    // console.log('📝 [Preprocess] Output:', JSON.stringify(processed.substring(0, 200)));
    
    return processed;
  };

  // Function to parse and replace <click> tags with clickable span elements.
  // Supports both legacy <click>label</click> and attributed
  // <click action="..." id="...">label</click> forms (see clickProtocol.js).
  const parseClickableTags = (text) => {
    const clickRegex = new RegExp(CLICK_TAG_RE.source, CLICK_TAG_RE.flags);
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = clickRegex.exec(text)) !== null) {
      const rawAttrs = match[1] || '';
      const innerLabel = match[2];
      const { action, id } = parseClickAttrs(rawAttrs);

      // Add text before the match
      if (match.index > lastIndex) {
        let beforeText = text.substring(lastIndex, match.index);
        
        // Remove list-style bullets that appear directly before <click> tags
        // This regex specifically targets bullets at the start of a line OR
        // bullets preceded by newline/whitespace (list markers), while preserving
        // markdown formatting like **bold**, *italic*, etc.
        
        // Pattern explanation:
        // - Match newline followed by optional whitespace and single bullet character
        // - OR match start of string with optional whitespace and single bullet character
        // - But only if followed by whitespace (not part of markdown like **text**)
        beforeText = beforeText.replace(/(\n\s*[•\*\-\+]\s+)|(^\s*[•\*\-\+]\s+)/g, '\n');
        
        // Only add if there's content after cleaning
        if (beforeText.trim()) {
          parts.push({
            type: 'text',
            content: beforeText
          });
        }
      }
      
      parts.push({
        type: 'clickable',
        content: innerLabel,
        action,
        id: `click-${Date.now()}-${Math.random()}`,
        clickId: id,
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      let remainingText = text.substring(lastIndex);
      
      // Clean up any trailing whitespace
      remainingText = remainingText.trim();
      
      if (remainingText) {
        parts.push({
          type: 'text',
          content: remainingText
        });
      }
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  const handleClickableClick = (part) => {
    if (typeof onClickableTextClick !== 'function') return;
    // Always pass a structured payload; legacy bare <click> tags simply have
    // undefined action/id and the parent decides how to handle that.
    onClickableTextClick({
      label: part.content,
      action: part.action,
      id: part.clickId,
    });
  };

  // Clean text for TTS by removing HTML tags
  const cleanTextForTTS = (text) => {
    const stripClick = new RegExp(CLICK_TAG_RE.source, CLICK_TAG_RE.flags);
    let cleanedText = text.replace(stripClick, '$2');
    // Remove any other HTML tags
    cleanedText = cleanedText.replace(/<[^>]*>/g, '');
    return cleanedText.trim();
  };

  const handlePlayClick = async () => {
    if (playState === 'playing' || playState === 'loading') {
      // Stop playback
      ttsService.stop();
      // State will be updated via the onStateChange callback
    } else {
      // Start playback - state will be updated via the onStateChange callback
      // Clean the text before sending to TTS
      const cleanText = cleanTextForTTS(message.text);
      console.log(`🔊 [Message ${message.id}] Playing TTS for:`, cleanText.substring(0, 50) + '...');
      await ttsService.speak(cleanText, message.id);
    }
  };

  return (
    <div className={`message ${isUser ? 'message-user' : 'message-bot'} ${isEnglish ? 'message-english' : ''} message-lang-${lang}`}>
      <div className="message-wrapper">
        <div className="message-header">
          {!isUser && (
            <div className="message-avatar">
              <img src={agentAvatarIcon} alt="Agent" className="agent-avatar-icon" />
            </div>
          )}
          <div className="message-time">
            {new Date(message.timestamp).toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </div>
        </div>
        <div className="message-content">
          <div className="message-text">
            {isUser ? (
              message.text
            ) : (
              <>
                {parseClickableTags(message.text).map((part, index) => {
                  if (part.type === 'clickable') {
                    // Compact inline pill for detail follow-ups whose label is
                    // a single word (e.g. "الشروط", "الرسوم") so they line up
                    // horizontally instead of stacking full-width.
                    const isCompact =
                      part.action === 'detail' &&
                      !/\s/.test((part.content || '').trim());
                    const className = isCompact
                      ? 'clickable-text clickable-text-compact'
                      : 'clickable-text';
                    return (
                      <span
                        key={part.id || index}
                        className={className}
                        data-action={part.action || undefined}
                        data-click-id={part.clickId || undefined}
                        onClick={() => handleClickableClick(part)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleClickableClick(part);
                          }
                        }}
                      >
                        {part.content}
                      </span>
                    );
                  } else {
                    return (
                      <ReactMarkdown 
                        key={index}
                        remarkPlugins={[remarkGfm]}
                        components={{
                          // Custom rendering for better Arabic RTL support
                          p: ({node, ...props}) => <p dir="auto" {...props} />,
                          ol: ({node, ...props}) => <ol dir="auto" {...props} />,
                          ul: ({node, ...props}) => <ul dir="auto" {...props} />,
                          li: ({node, ...props}) => <li dir="auto" {...props} />,
                          td: ({node, ...props}) => <td dir="auto" {...props} />,
                          th: ({node, ...props}) => <th dir="auto" {...props} />,
                          // Open links in a new window
                          a: ({node, ...props}) => <a target="_blank" rel="noopener noreferrer" {...props} />,
                        }}
                      >
                        {preprocessListFormat(part.content)}
                      </ReactMarkdown>
                    );
                  }
                })}
              </>
            )}
          </div>
          {!isUser && message.sender === 'bot' && (
            <div className="message-actions">
              <button 
                className={`play-button ${playState}`}
                onClick={handlePlayClick}
                aria-label={playState === 'playing' ? (lang === 'ar' ? 'إيقاف' : 'Stop') : (lang === 'ar' ? 'تشغيل' : 'Play')}
              >
                {playState === 'loading' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.3"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                    </path>
                  </svg>
                ) : playState === 'playing' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="6" y="4" width="4" height="16" fill="currentColor" rx="1"/>
                    <rect x="14" y="4" width="4" height="16" fill="currentColor" rx="1"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5v14l11-7z" fill="currentColor"/>
                  </svg>
                )}
              </button>
              
              <button
                type="button"
                className={`feedback-button ${feedback === 'like' ? 'active' : ''}`}
                disabled={isSubmittingFeedback}
                onClick={() => handleFeedbackClick('like')}
                aria-label={lang === 'ar' ? 'إعجاب' : 'Like response'}
                aria-pressed={feedback === 'like'}
                title={lang === 'ar' ? 'إعجاب' : 'Like'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill={feedback === 'like' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                </svg>
              </button>
              
              <button
                type="button"
                className={`feedback-button ${feedback === 'dislike' ? 'active' : ''}`}
                disabled={isSubmittingFeedback}
                onClick={() => handleFeedbackClick('dislike')}
                aria-label={lang === 'ar' ? 'عدم إعجاب' : 'Dislike response'}
                aria-pressed={feedback === 'dislike'}
                title={lang === 'ar' ? 'عدم إعجاب' : 'Dislike'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill={feedback === 'dislike' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Message;

